/**
 * Scoped JWT utilities
 *
 * After registration the API returns a short-lived `scopedJwt` that grants
 * access only to the installment-payment endpoint.  We keep it in
 * sessionStorage (not localStorage) so it is automatically discarded when
 * the browser tab is closed and never persisted to disk alongside the real
 * auth token.
 */

const SCOPED_JWT_KEY = 'scopedJwt';
const PENDING_CHECKOUT_COURSE_KEY = 'pendingCheckoutCourseId';

/**
 * Persist the scoped JWT returned by POST /api/auth/local/register.
 * @param {string} token
 */
export const storeScopedJwt = (token) => {
  if (!token) return;
  try {
    sessionStorage.setItem(SCOPED_JWT_KEY, token);
  } catch (err) {
    console.error('Failed to store scopedJwt:', err);
  }
};

/**
 * Retrieve the scoped JWT from sessionStorage.
 * @returns {string|null}
 */
export const getScopedJwt = () => {
  try {
    return sessionStorage.getItem(SCOPED_JWT_KEY);
  } catch (err) {
    console.error('Failed to read scopedJwt:', err);
    return null;
  }
};

/**
 * Remove the scoped JWT from sessionStorage.
 * Call this after a successful installment-payment response.
 */
export const clearScopedJwt = () => {
  try {
    sessionStorage.removeItem(SCOPED_JWT_KEY);
  } catch (err) {
    console.error('Failed to clear scopedJwt:', err);
  }
};

/**
 * Return the best available bearer token for installment-payment requests:
 * prefer the full auth token (cookie/localStorage), fall back to scopedJwt.
 *
 * @param {string|null} fullAuthToken - The regular session token if the user
 *   is already fully authenticated.
 * @returns {string|null}
 */
export const getInstallmentBearerToken = (fullAuthToken) => {
  if (fullAuthToken) return fullAuthToken;
  return getScopedJwt();
};

// ---------------------------------------------------------------------------
// Pending checkout course helpers
// ---------------------------------------------------------------------------

/**
 * Persist the course ID the user was attempting to purchase at registration
 * time so the verify-email page can offer a "Return to payment" deep-link.
 * Stored in sessionStorage only — discarded when the tab closes.
 *
 * @param {string|number} courseId
 */
export const storePendingCheckoutCourseId = (courseId) => {
  if (!courseId && courseId !== 0) return;
  try {
    sessionStorage.setItem(PENDING_CHECKOUT_COURSE_KEY, String(courseId));
  } catch (err) {
    console.error('Failed to store pendingCheckoutCourseId:', err);
  }
};

/**
 * Retrieve the pending checkout course ID from sessionStorage.
 * @returns {string|null}
 */
export const getPendingCheckoutCourseId = () => {
  try {
    return sessionStorage.getItem(PENDING_CHECKOUT_COURSE_KEY);
  } catch (err) {
    console.error('Failed to read pendingCheckoutCourseId:', err);
    return null;
  }
};

/**
 * Remove the pending checkout course ID from sessionStorage.
 * Call this after the user verifies their email or abandons the checkout.
 */
export const clearPendingCheckoutCourseId = () => {
  try {
    sessionStorage.removeItem(PENDING_CHECKOUT_COURSE_KEY);
  } catch (err) {
    console.error('Failed to clear pendingCheckoutCourseId:', err);
  }
};
